import { CircularProgress, Divider } from "@suraasa/placebo-ui"
import Fallback from "components/Fallback"

import OverallGPA from "./OverallGPA"
import ProgressTracker from "./ProgressTracker"
import TimeSpent from "./TimeSpent"
import { timeIntervalOptions } from "./types"
import { useOverviewAnalytics } from "./useOverviewAnalytics"

type OverviewAnalytics = ReturnType<typeof useOverviewAnalytics>

type Props = {
  isCourse: boolean
} & Pick<
  OverviewAnalytics,
  | "analyticsData"
  | "isAnalyticsError"
  | "timeSpentQuery"
  | "selectedTimeInterval"
  | "setSelectedTimeInterval"
  | "sortedTerms"
>

/** The Progress + Time Spent + Overall GPA card shared by the Qualification
 * and Course Overview pages. */
const OverviewStatsCard = ({
  isCourse,
  analyticsData,
  isAnalyticsError,
  timeSpentQuery,
  selectedTimeInterval,
  setSelectedTimeInterval,
  sortedTerms,
}: Props) => {
  if (isAnalyticsError) {
    return (
      <Fallback
        className="rounded-2xl border border-onSurface-200"
        hasError
        data={{ title: "Something went wrong" }}
      />
    )
  }

  return (
    <div className="flex flex-col rounded-2xl border border-onSurface-200 sm:flex-row">
      <div className="w-full sm:w-1/2">
        <ProgressTracker
          progress={analyticsData?.progress}
          terms={isCourse ? null : sortedTerms}
          filteredTerm=""
          filteredModule=""
          isCourse={isCourse}
        />
      </div>

      <div className="hidden gap-1.25 sm:flex">
        <Divider orientation="vertical" className="bg-onSurface-200" />
      </div>
      <Divider className="block bg-onSurface-200 sm:hidden" />

      <div className="flex w-full flex-col sm:w-1/2">
        <div className="p-2.5">
          <TimeSpent
            isCourse={isCourse}
            weeklyTimeSpentData={
              isCourse
                ? undefined
                : timeSpentQuery.data?.intervals?.map(
                    interval => interval.totalTime
                  )
            }
            totalTimeSpent={timeSpentQuery.data?.totalTimeSpent}
            weeklyTimeSpent={timeSpentQuery.data?.avgWeeklyTimeSpent}
            selectedTimeInterval={selectedTimeInterval}
            setSelectedTimeInterval={setSelectedTimeInterval}
            timeIntervalOptions={timeIntervalOptions}
            timeGraphLoading={isCourse ? undefined : timeSpentQuery.isLoading}
            thisWeekTimeSpent={
              isCourse
                ? undefined
                : timeSpentQuery.data?.intervals &&
                  timeSpentQuery.data?.intervals[0]?.totalTime
            }
          />
        </div>
        <Divider className="bg-onSurface-200" />
        <OverallGPA
          gpa={analyticsData?.gpa}
          isCourse={isCourse}
          filteredCourseSlug={undefined}
        />
      </div>
    </div>
  )
}

export default OverviewStatsCard
